import type { PatientResponseDto, SessionDetailDto } from "~/lib/api";

/**
 * Edit-permission rules (HAN-49): the single source of truth for "may this
 * caller add / edit / acknowledge this patient?". The list, detail and form
 * screens gate their buttons and menus on these helpers — no screen re-derives
 * the rule inline. The API enforces the identical rules authoritatively
 * (PatientEditableGuard → the service → the DB immutability triggers), so a
 * caller who slips past the UI is still refused server-side; these helpers just
 * keep the UI from offering an action the caller can't complete.
 *
 * Every helper takes plain data objects (no API calls inside), so they're
 * unit-testable in isolation.
 */

/** The caller, identified by their application user id (not the Firebase uid). */
export interface PermissionUser {
  id: string;
}

/** The session fields the rules depend on. */
type PermissionSession = Pick<SessionDetailDto, "status" | "outgoingDoctorId" | "incomingDoctorId">;

/** The patient fields the rules depend on. */
type PermissionPatient = Pick<PatientResponseDto, "acknowledgedAt">;

/** A session still open to edits: patients may be added/changed until it locks. */
function isSessionOpen(session: PermissionSession): boolean {
  return session.status === "draft" || session.status === "active";
}

/**
 * Whether the caller is the session's outgoing doctor — the only role that may
 * add or edit patients. Identity only: it says nothing about the session status
 * or acknowledgement (see {@link canAddPatient} / {@link canEditPatient}). The
 * form uses this on its own to decide whether to reveal the form at all.
 */
export function isOutgoingDoctor(user: PermissionUser | null, session: PermissionSession): boolean {
  return user != null && user.id === session.outgoingDoctorId;
}

/**
 * Whether the caller may add a patient to the session: the outgoing doctor, on
 * a session that is still open (draft or active).
 */
export function canAddPatient(user: PermissionUser | null, session: PermissionSession): boolean {
  return isOutgoingDoctor(user, session) && isSessionOpen(session);
}

/**
 * Whether the caller may edit this patient: the outgoing doctor, on an open
 * session, for a patient that has not yet been acknowledged.
 */
export function canEditPatient(
  user: PermissionUser | null,
  session: PermissionSession,
  patient: PermissionPatient,
): boolean {
  return canAddPatient(user, session) && patient.acknowledgedAt === null;
}

/**
 * Whether the caller may acknowledge this patient (used in E7): the incoming
 * doctor, on an active session, for a patient not already acknowledged.
 */
export function canAcknowledgePatient(
  user: PermissionUser | null,
  session: PermissionSession,
  patient: PermissionPatient,
): boolean {
  return (
    user != null &&
    session.status === "active" &&
    user.id === session.incomingDoctorId &&
    patient.acknowledgedAt === null
  );
}

/**
 * Whether the caller may record the overnight update for this session's patients
 * (V2-E8.1): the incoming doctor only. Deliberately *not* gated on session
 * status or acknowledgement — the overnight update is written during the shift,
 * after the handover has been received and (typically) the session has locked.
 * The API enforces the identical rule.
 */
export function canRecordOvernightUpdate(
  user: PermissionUser | null,
  session: PermissionSession,
): boolean {
  return user != null && user.id === session.incomingDoctorId;
}

/**
 * Why a patient is frozen for editing, or `null` if it isn't (regardless of who
 * the caller is). A locked session freezes every patient; otherwise an
 * acknowledged patient is frozen. A null patient (the create screen) is never
 * frozen by acknowledgement. The session lock takes precedence when both apply.
 * The form uses this to show the right "locked" banner.
 */
export type EditLockReason = "session-locked" | "acknowledged" | null;

export function patientEditLockReason(
  session: PermissionSession,
  patient: PermissionPatient | null,
): EditLockReason {
  if (session.status === "locked") return "session-locked";
  if (patient?.acknowledgedAt != null) return "acknowledged";
  return null;
}
